/**
 * Back-office BCL — paramétrage des prix (étape 7 du cahier des charges).
 * Permet à un admin de modifier nom, description, fourchette HT et activation
 * de chaque pack. Le pack reste dans la liste même désactivé pour pouvoir
 * être ré-activé.
 */
import { updatePackSchema, type Pack, type UpdatePackInput } from '@bcl/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import { listAllPacks, updatePack } from '@/features/admin/packs-api';
import { extractErrorMessage } from '@/features/auth/error-message';

function formatPriceRange(min: number, max: number): string {
  if (min === max) return `${min} € HT`;
  return `${min} € — ${max} € HT`;
}

export function AdminPacksPage() {
  const qc = useQueryClient();
  const packs = useQuery({ queryKey: ['admin', 'packs'], queryFn: listAllPacks });
  const [editing, setEditing] = useState<Pack | null>(null);

  return (
    <DashboardLayout variant="admin" title="Packs & tarifs">
      <Card>
        <CardHeader>
          <CardTitle>Packs commerciaux</CardTitle>
          <CardDescription>
            Paramétrage des prix HT et de l'activation des packs proposés
            durant l'étape 7 du parcours client.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Nom</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Fourchette HT</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {packs.data?.length ? (
                packs.data.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-mono text-xs">{p.code}</TableCell>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell className="max-w-md text-sm text-gray-700">
                      {p.description}
                    </TableCell>
                    <TableCell>
                      {formatPriceRange(p.priceMinHt, p.priceMaxHt)}
                    </TableCell>
                    <TableCell>
                      {p.active ? (
                        <Badge className="bg-emerald-100 text-emerald-800">
                          Actif
                        </Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-700">
                          Désactivé
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditing(p)}
                        aria-label={`Éditer ${p.name}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-sm text-gray-500">
                    {packs.isLoading ? 'Chargement…' : 'Aucun pack.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {editing && (
        <EditPackDialog
          pack={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['admin', 'packs'] });
            qc.invalidateQueries({ queryKey: ['public', 'packs'] });
            setEditing(null);
          }}
        />
      )}
    </DashboardLayout>
  );
}

function EditPackDialog({
  pack,
  onClose,
  onSaved,
}: {
  pack: Pack;
  onClose: () => void;
  onSaved: () => void;
}) {
  const form = useForm<UpdatePackInput>({
    resolver: zodResolver(updatePackSchema),
    defaultValues: {
      name: pack.name,
      description: pack.description,
      priceMinHt: pack.priceMinHt,
      priceMaxHt: pack.priceMaxHt,
      active: pack.active,
    },
  });

  useEffect(() => {
    form.reset({
      name: pack.name,
      description: pack.description,
      priceMinHt: pack.priceMinHt,
      priceMaxHt: pack.priceMaxHt,
      active: pack.active,
    });
  }, [pack, form]);

  const mut = useMutation({
    mutationFn: (input: UpdatePackInput) => updatePack(pack.code, input),
    onSuccess: () => {
      toast.success('Pack mis à jour');
      onSaved();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const active = form.watch('active');

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Éditer le pack</DialogTitle>
          <DialogDescription>{pack.name}</DialogDescription>
        </DialogHeader>
        <form
          onSubmit={form.handleSubmit((values) => mut.mutate(values))}
          className="space-y-4"
        >
          <div className="space-y-1.5">
            <Label htmlFor="pack-name">Nom</Label>
            <Input id="pack-name" {...form.register('name')} />
            {form.formState.errors.name && (
              <p className="text-xs text-destructive">
                {form.formState.errors.name.message}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pack-description">Description</Label>
            <Textarea
              id="pack-description"
              rows={3}
              {...form.register('description')}
            />
            {form.formState.errors.description && (
              <p className="text-xs text-destructive">
                {form.formState.errors.description.message}
              </p>
            )}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="pack-min">Prix min HT (€)</Label>
              <Input
                id="pack-min"
                type="number"
                step="0.01"
                min={0}
                {...form.register('priceMinHt', { valueAsNumber: true })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pack-max">Prix max HT (€)</Label>
              <Input
                id="pack-max"
                type="number"
                step="0.01"
                min={0}
                {...form.register('priceMaxHt', { valueAsNumber: true })}
              />
            </div>
          </div>
          {form.formState.errors.priceMinHt && (
            <p className="text-xs text-destructive">
              {form.formState.errors.priceMinHt.message}
            </p>
          )}
          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <Label htmlFor="pack-active" className="cursor-pointer">
                Pack actif
              </Label>
              <p className="text-xs text-gray-500">
                Affiché dans le sélecteur de pack (étape 7).
              </p>
            </div>
            <button
              type="button"
              role="switch"
              id="pack-active"
              aria-checked={active ?? false}
              onClick={() => form.setValue('active', !active)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                active ? 'bg-bcl-blue' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  active ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button type="submit" disabled={mut.isPending}>
              {mut.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
